/**
 * ForeignKartotekaViewModal — projekt formularza / listy dla pozycji menu modułu wskazującej
 * kartotekę SPOZA modułu (np. Umowy w menu Kadr; kreator ITE, decyzja 2026-10-05).
 *
 * Osadza te same kreatory układów co ekran „Kartoteki" w panelu ITE (biblioteka kartoteki),
 * ale BEZ zapisu do biblioteki: gotowy config wraca do kreatora menu (`onSave`) i trafia do
 * bazy razem z „Zapisz menu" — jak układy kartotek własnych modułu.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  CreatorKartotekaLayoutsInfo,
  KartotekaViewDto,
  ListConfig,
  ModuleTemplateFormConfig,
} from '@saas/shared';

import { KartotekaViewFormBuilder } from '@/components/kartoteki/KartotekaViewFormBuilder';
import { KartotekaViewListBuilder } from '@/components/kartoteki/KartotekaViewListBuilder';
import {
  type KartotekaBuilderField,
  type KartotekaDefaultListConfig,
  type KartotekaIdentityRef,
  useKartotekaDefinition,
} from '@/lib/kartoteki/kartoteka-definition';

import { BuilderOverlay } from './BuilderOverlay';

export interface ForeignKartotekaViewDraft {
  slug: string;
  name: string;
  viewType: 'form' | 'list';
  config: ModuleTemplateFormConfig | ListConfig | null;
  sortOrder: number;
}

interface ForeignKartotekaViewModalProps {
  /** Kod kartoteki (= `moduleCode` pozycji menu). */
  moduleCode: string;
  /** Biblioteka układów: systemowa (slug) albo kartoteka z kreatora (UUID encji). */
  identity: KartotekaIdentityRef;
  /** Kartoteka z kreatora: nazwa i pola z opublikowanej wersji modułu-źródła. */
  creatorInfo?: CreatorKartotekaLayoutsInfo;
  /** Nazwa pozycji w menu (nagłówek okna). */
  itemLabel: string;
  view: ForeignKartotekaViewDraft;
  onSave: (config: ModuleTemplateFormConfig | ListConfig) => void;
  onClose: () => void;
}

export function ForeignKartotekaViewModal({
  moduleCode,
  identity,
  creatorInfo,
  itemLabel,
  view,
  onSave,
  onClose,
}: ForeignKartotekaViewModalProps) {
  const { t } = useTranslation();
  // Kartoteka systemowa: pola z definicji kartoteki; z kreatora: z opublikowanej wersji.
  const definition = useKartotekaDefinition(identity.kind === 'system' ? moduleCode : undefined);
  // Kreatory biblioteki operują na DTO widoku — składamy go z wersji roboczej.
  const [dto] = useState<KartotekaViewDto>(() => ({
    id: `draft-${view.slug}`,
    kartotekaKind: identity.kind,
    kartotekaKey: identity.key,
    slug: view.slug,
    name: view.name,
    viewType: view.viewType,
    config: (view.config as KartotekaViewDto['config']) ?? null,
    sortOrder: view.sortOrder,
    firstPublishedAt: null,
    createdAt: '',
    updatedAt: '',
  }));
  const creatorFields: KartotekaBuilderField[] = (creatorInfo?.fields ?? []).map((f) => ({
    id: f.slug,
    slug: f.slug,
    name: f.name,
    fieldType: f.type,
    isCore: false,
    isSystemLocked: false,
    isRequired: f.isRequired,
    sortOrder: f.sortOrder,
  }));
  const fields = creatorInfo ? creatorFields : (definition.data?.fields ?? []);
  const fieldsReady = creatorInfo ? true : definition.isSuccess;
  // Nowa lista startuje od jednej kolumny — nazwy rekordu (decyzja 2026-10-05): kartoteka
  // z kreatora ma ją wprost, systemowa — pierwszą kolumnę swojej domyślnej listy.
  const defaultListConfig = definition.data?.defaultListConfig;
  const startListConfig: KartotekaDefaultListConfig | undefined = creatorInfo
    ? {
        columns: [{ id: 'record_name', visible: true, width: 'auto', order: 0 }],
        defaultSort: null,
        defaultFilters: [] as [],
        formattingRules: [] as [],
        quickFilters: [] as [],
        defaultViewMode: 'list' as const,
        widgets: [] as [],
      }
    : defaultListConfig
      ? { ...defaultListConfig, columns: defaultListConfig.columns.slice(0, 1) }
      : undefined;

  // Formularz zapisuje się jednym przyciskiem → zamykamy okno. Lista ma zakładki (kolumny,
  // filtry…) zapisywane osobno → okno zostaje otwarte, zamyka je użytkownik.
  const handleSave = (config: ModuleTemplateFormConfig | ListConfig) => {
    onSave(config);
    if (view.viewType === 'form') onClose();
  };

  return (
    // WYJĄTEK od wspólnej zasady: tło i Esc NIE zamykają tego okna (zamyka X lub „Wstecz"). Okno
    // nie ma sygnału niezapisanych zmian, a X zamyka bez pytania — przypadkowe zamknięcie tłem
    // kosztowałoby pracę nad układem (decyzja 2026-10-16).
    <BuilderOverlay
      testId="foreign-kartoteka-view-modal"
      onRequestClose={onClose}
      dismissible={false}
      width="wide"
      title={
        view.viewType === 'form'
          ? t('admin.menuItemViews.foreign.formTitle')
          : t('admin.menuItemViews.foreign.listTitle')
      }
      subtitle={`${itemLabel} · ${t('admin.menuItemViews.foreign.sharedHint')}`}
      closeLabel={t('admin.templateMenuBuilder.viewBuilder.close')}
      closeTestId="foreign-kartoteka-view-close"
    >
      {!creatorInfo && definition.isLoading ? (
        <p className="text-sm text-text-muted">…</p>
      ) : view.viewType === 'form' ? (
        <KartotekaViewFormBuilder
          identity={identity}
          fields={fields}
          view={dto}
          onBack={onClose}
          onSaveConfig={handleSave}
        />
      ) : (
        <KartotekaViewListBuilder
          identity={identity}
          view={dto}
          fields={fields}
          fieldsReady={fieldsReady}
          defaultListConfig={startListConfig}
          onBack={onClose}
          onSaveConfig={handleSave}
        />
      )}
    </BuilderOverlay>
  );
}
